// ActivityTwoWordInputs.jsx — Book activity (Phase 3: real logic).
// Matches room.php's #myModal2 / .inputs-container behavior: live
// green/red-as-you-type feedback (keyup handler in the PHP JS), Submit only
// enabled once all 4 fields are filled, case-insensitive/trimmed matching,
// an answer only validates in its own field (each field is its own
// controlled input, so this is true by construction), 1 point per correct
// field, hint popups open per-field and close on OK.
import React, { useRef, useState } from 'react';
import ActivityPopup from '../overlays/ActivityPopup';
import GamePopup from '../overlays/GamePopup';
import HintPopup from '../overlays/HintPopup';
import '../overlays/HintPopup.css';
import {
  BOOK_TITLE,
  BOOK_SUBTITLE,
  BOOK_FIELDS,
  BOOK_CORRECT_ANSWER_TITLE,
} from '../../../games/escape-to-inner-peace/data/gameText';
import '../../../games/escape-to-inner-peace/styles/theme.css';
import './ActivityTwoWordInputs.css';

const FEEDBACK_DELAY_MS = 1500;

function formatHint(hint, answer) {
  return hint.replace(answer, '_________');
}

function normalize(value) {
  return value.trim().toLowerCase();
}

function capitalize(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

// room.php always lists all 4 correct answers, in fixed field order,
// capitalized — regardless of which specific fields were wrong.
const CORRECT_ANSWERS_TEXT = BOOK_FIELDS.map((field) => capitalize(field.answer)).join(', ');

function ActivityTwoWordInputs({ open, onComplete }) {
  const [values, setValues] = useState({ 1: '', 2: '', 3: '', 4: '' });
  const [openHintId, setOpenHintId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [feedback, setFeedback] = useState({});
  // QA fix (Bug 2) — room.php shows a "Correct answer" SweetAlert (listing
  // idea/mind/time/team) before the learning popup whenever the submission
  // has any wrong answer; a perfect submission skips it. This is
  // self-contained to Book: onComplete (which drives GamePage's scoring +
  // learning-popup sequencing) only fires once this popup is dismissed.
  const [showCorrectAnswerPopup, setShowCorrectAnswerPopup] = useState(false);
  const pendingCorrectCountRef = useRef(0);
  // ESC-QA-19 — a redundant guard on the OK button itself (double-click,
  // rapid Enter, touch double-tap): every other transition in this
  // component already checks `submitted` first, but this one had nothing
  // stopping a second invocation from firing onComplete/the score/the
  // modal transition twice before React can unmount the popup.
  const correctAnswerClosingRef = useRef(false);

  const openHint = BOOK_FIELDS.find((f) => f.id === openHintId);
  const allFilled = BOOK_FIELDS.every((field) => values[field.id].trim() !== '');

  const handleChange = (id, value) => {
    if (submitted) return;
    // No leading spaces, matching room.php's input handler.
    setValues((prev) => ({ ...prev, [id]: value.replace(/^\s+/, '') }));
  };

  const liveState = (field) => {
    const value = values[field.id];
    if (value.trim() === '') return 'empty';
    return normalize(value) === normalize(field.answer) ? 'correct-live' : 'incorrect-live';
  };

  const handleSubmit = () => {
    if (submitted || !allFilled) return;
    setSubmitted(true);
    // ESC-QA-13 — only one Book modal should ever be active at a time.
    // Feedback/the "Correct answer" popup is about to take over (after
    // FEEDBACK_DELAY_MS below); close any hint that's still open now,
    // before that window, rather than letting it linger and get buried
    // underneath the next popup.
    setOpenHintId(null);

    let correctCount = 0;
    const nextFeedback = {};
    BOOK_FIELDS.forEach((field) => {
      const isCorrect = normalize(values[field.id]) === normalize(field.answer);
      nextFeedback[field.id] = isCorrect ? 'correct' : 'incorrect';
      if (isCorrect) correctCount += 1;
    });
    setFeedback(nextFeedback);

    if (correctCount < BOOK_FIELDS.length) {
      pendingCorrectCountRef.current = correctCount;
      setTimeout(() => {
        setShowCorrectAnswerPopup(true);
      }, FEEDBACK_DELAY_MS);
    } else {
      setTimeout(() => {
        onComplete(correctCount);
      }, FEEDBACK_DELAY_MS);
    }
  };

  const handleCorrectAnswerClose = () => {
    if (correctAnswerClosingRef.current) return;
    correctAnswerClosingRef.current = true;
    setShowCorrectAnswerPopup(false);
    onComplete(pendingCorrectCountRef.current);
  };

  return (
    <ActivityPopup
      open={open}
      title={BOOK_TITLE}
      subtitle={BOOK_SUBTITLE}
      plainHeader
      boldTitle={false}
      testId="escape-activity-book"
      footer={
        <button
          type="button"
          className="escape-submit-btn"
          disabled={!allFilled || submitted}
          aria-label="Submit"
          data-testid="escape-book-submit"
          onClick={handleSubmit}
        />
      }
    >
      <div className="escape-inputs-container">
        {BOOK_FIELDS.map((field) => {
          const state = submitted ? feedback[field.id] : liveState(field);
          return (
            <div className="escape-input-wrapper" key={field.id}>
              <button
                type="button"
                className="escape-info-button"
                aria-label={`Hint for field ${field.id}`}
                data-testid={`escape-book-hint-${field.id}`}
                disabled={submitted}
                onClick={() => {
                  if (submitted) return;
                  setOpenHintId(field.id);
                }}
              >
                i
              </button>
              <input
                type="text"
                aria-label={`Answer field ${field.id}`}
                data-testid={`escape-book-input-${field.id}`}
                className={`escape-book-input escape-book-input--${state}`}
                value={values[field.id]}
                onChange={(e) => handleChange(field.id, e.target.value)}
                readOnly={submitted || state === 'correct-live'}
              />
            </div>
          );
        })}
      </div>

      <HintPopup
        open={Boolean(openHint)}
        onClose={() => setOpenHintId(null)}
        text={openHint ? formatHint(openHint.hint, openHint.answer) : ''}
      />

      <GamePopup
        open={showCorrectAnswerPopup}
        onClose={handleCorrectAnswerClose}
        size="small"
        ariaLabel={BOOK_CORRECT_ANSWER_TITLE}
        closeButton={false}
        testId="escape-book-correct-answer-popup"
      >
        <h3 className="escape-book-correct-answer-title">{BOOK_CORRECT_ANSWER_TITLE}</h3>
        <div className="escape-book-correct-answer-text">{CORRECT_ANSWERS_TEXT}</div>
        <button
          type="button"
          className="escape-hint-ok-btn"
          onClick={handleCorrectAnswerClose}
          data-testid="escape-book-correct-answer-ok"
        >
          OK
        </button>
      </GamePopup>
    </ActivityPopup>
  );
}

export default ActivityTwoWordInputs;
